<template>
    <div class="contact-detail-item">
        <div class="contact-detail-item-details">
            <div class="contact-detail-item-icon">
                <slot name="icon" />
            </div>
            <div class="contact-detail-item-input">
                <div class="contact-detail-item-input-label">{{ label }}</div>
                <div
                    class="contact-detail-item-input-val"
                    v-if="value"
                    v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [600, 0] }"
                    :content="getTippyValue()"
                >
                    <template v-if="field === 'tags'">
                        <div class="tags-container">
                            <span
                                v-for="tag in mappedTags"
                                :key="tag._id"
                                :style="getTagStyle(tag.color)"
                                class="tag-label"
                            >
                                <span class="remove-tag" @click="removeTag(tag._id)">×</span>
                                {{ tag.name }}
                            </span>
                        </div>
                    </template>
                    <template v-else>
                        {{ value }}
                    </template>
                </div>
                <div class="contact-detail-item-input-val add-link" v-else-if="showAdd" @click="showAddModal">
                    + Ekle
                </div>
            </div>
        </div>
        <div class="contact-detail-item-actions" v-if="value">
            <button v-if="showEdit" class="action-button edit-btn" @click="handleEdit">
                <span class="material-icons">edit</span>
            </button>
            <button v-if="showDelete" class="action-button delete-btn" @click="showDeleteModal">
                <span class="material-icons">delete</span>
            </button>
            <button v-if="showCopy" class="action-button copy-btn" @click="copyValue">
                <span class="material-icons">content_copy</span>
            </button>
            <slot name="actions"></slot>
        </div>

        <Modal2
            v-if="editModal && !isSpecialField"
            :title="'Düzenle: ' + label"
            @cancel="editModal = false"
            :showFooter="false"
        >
            <div id="contact-property-modal-content">
                <input v-model="editedValue" :placeholder="'Yeni değer girin'" />
                <div class="modal-actions">
                    <SupsisButton @click="editModal = false">Vazgeç</SupsisButton>
                    <SupsisButton @click="saveEdit" type="primary">Güncelle</SupsisButton>
                </div>
            </div>
        </Modal2>

        <Modal2
            v-if="editModal && field === 'preferredLang'"
            :title="'Tercih Edilen Dili Güncelle'"
            @cancel="editModal = false"
            :showFooter="false"
        >
            <UpdateContactLang :contact="contact" @close="editModal = false" @save="savePreferredLang" />
        </Modal2>

        <Modal2
            v-if="editModal && field === 'tags'"
            :title="'Etiketleri Güncelle'"
            @cancel="editModal = false"
            :showFooter="false"
        >
            <TagDialog
                :value="getContactTagIds()"
                :inlineMode="true"
                @input="saveTagsUpdate"
                @close="editModal = false"
            />
        </Modal2>

        <Modal2
            v-if="editModal && field === 'email'"
            :title="'E-posta Güncelle'"
            @cancel="editModal = false"
            :showFooter="false"
        >
            <div id="contact-property-modal-content">
                <input v-model="editedValue" type="email" :placeholder="'Yeni e-posta girin'" />
                <div v-if="!editedValue.includes('@')" class="email-suggestions">
                    <span v-for="domain in emailDomains" :key="domain" @click="appendEmailDomain(domain)">
                        {{ editedValue }}{{ domain }}
                    </span>
                </div>
                <div class="modal-actions">
                    <SupsisButton @click="editModal = false">Vazgeç</SupsisButton>
                    <SupsisButton @click="saveEdit" type="primary">Güncelle</SupsisButton>
                </div>
            </div>
        </Modal2>

        <Modal2 v-if="addModal" :title="'Ekle: ' + label" @cancel="addModal = false" :showFooter="false">
            <div id="contact-property-modal-content">
                <input v-model="newValue" :placeholder="'Yeni değer girin'" />
                <div class="modal-actions">
                    <SupsisButton @click="addModal = false">Vazgeç</SupsisButton>
                    <SupsisButton @click="saveAdd" type="primary">Ekle</SupsisButton>
                </div>
            </div>
        </Modal2>

        <Modal2 v-if="deleteModal" title="Silme Onayı" @cancel="deleteModal = false" :showFooter="false">
            <div id="contact-property-modal-content">
                <p>Bu özelliği silmek istediğinizden emin misiniz?</p>
                <div class="modal-actions">
                    <SupsisButton @click="deleteModal = false">Vazgeç</SupsisButton>
                    <SupsisButton @click="confirmDelete" type="primary">Evet, Sil</SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { copyText2Clipboard, SanitizeHtml } from "../../../utils/utils";
import Modal2 from "../../common/Modal2.vue";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import UpdateContactLang from "./UpdateContactLang.vue";
import TagDialog from "../../TagDialog.vue";
import { mapState } from "vuex";

export default {
    name: "ContactDetailItem",
    components: {
        Modal2,
        SupsisButton,
        UpdateContactLang,
        TagDialog,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
        label: {
            type: String,
            default: null,
        },
        value: {
            type: [String, Number, Array],
            default: null,
        },
        field: {
            type: String,
            default: null,
        },
        showEdit: {
            type: Boolean,
            default: true,
        },
        showDelete: {
            type: Boolean,
            default: true,
        },
        showCopy: {
            type: Boolean,
            default: true,
        },
        showAdd: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            editModal: false,
            addModal: false,
            deleteModal: false,
            editedValue: "",
            newValue: "",
            emailDomains: ["@gmail.com", "@yahoo.com", "@outlook.com", "@hotmail.com"],
        };
    },
    computed: {
        ...mapState({
            allTags: (state) => state.tag.tags.data,
        }),
        isSpecialField() {
            return ["preferredLang", "tags", "email"].includes(this.field);
        },
        mappedTags() {
            if (this.field !== "tags" || !this.value) return [];
            return this.value.map((tagId) => this.allTags.find((tag) => tag._id === tagId)).filter((tag) => tag);
        },
    },
    methods: {
        isValue() {
            return this.$slots.value;
        },
        getTippyValue() {
            if (this.field === "tags") {
                return this.mappedTags
                    .map(
                        (tag) =>
                            `<div style="background-color: ${tag.color}; padding: 2px 5px; border-radius: 3px;">${tag.name}</div>`
                    )
                    .join("");
            }
            const sanitizedHtml = SanitizeHtml(this.getValue()) || "N/A";
            return `<div style="text-align: start; font-weight: bold">${this.label}</div>${sanitizedHtml}`;
        },
        getValue() {
            return SanitizeHtml(this.value) || "N/A";
        },
        copyValue() {
            copyText2Clipboard(this.getValue());
            this.$message.success("İçerik başarıyla kopyalandı!");
        },
        handleEdit() {
            if (this.isSpecialField) {
                this.editedValue = this.value;
                this.editModal = true;
            } else {
                this.showEditModal();
            }
        },
        showEditModal() {
            this.editedValue = this.value;
            this.editModal = true;
        },
        showAddModal() {
            this.newValue = "";
            this.addModal = true;
        },
        showDeleteModal() {
            this.deleteModal = true;
        },
        saveEdit() {
            this.$emit("edit", this.editedValue);
            this.editModal = false;
        },
        saveAdd() {
            this.$emit("add", this.newValue);
            this.addModal = false;
        },
        confirmDelete() {
            this.$emit("delete");
            this.deleteModal = false;
        },
        savePreferredLang(newLang) {
            this.$emit("edit", newLang);
            this.editModal = false;
        },
        saveTagsUpdate(newTags) {
            this.$emit("edit", newTags);
            this.editModal = false;
        },
        getContactTagIds() {
            return this.contact.tags || [];
        },
        getTagStyle(color) {
            const isDark = this.isDarkColor(color);
            return {
                backgroundColor: this.lightenColor(color, 0.3),
                color: isDark ? "white" : "black",
                borderRadius: "12px",
                padding: "2px 8px",
                marginRight: "5px",
                display: "inline-flex",
                alignItems: "center",
                maxWidth: "100%",
                flexShrink: 0,
            };
        },
        isDarkColor(color) {
            const c = color.substring(1); // strip #
            const rgb = parseInt(c, 16); // convert rrggbb to decimal
            const r = (rgb >> 16) & 0xff; // extract red
            const g = (rgb >> 8) & 0xff; // extract green
            const b = (rgb >> 0) & 0xff; // extract blue
            const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
            return luma < 128;
        },
        lightenColor(color, percent) {
            const num = parseInt(color.slice(1), 16),
                amt = Math.round(2.55 * percent),
                R = (num >> 16) + amt,
                G = ((num >> 8) & 0x00ff) + amt,
                B = (num & 0x0000ff) + amt;
            return `#${(
                0x1000000 +
                (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                (B < 255 ? (B < 1 ? 0 : B) : 255)
            )
                .toString(16)
                .slice(1)}`;
        },
        removeTag(tagId) {
            const newTags = this.value.filter((id) => id !== tagId);
            this.$emit("edit", newTags);
        },
        appendEmailDomain(domain) {
            this.editedValue += domain;
        },
    },
};
</script>

<style lang="scss" scoped>
.contact-detail-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #e0e0e0;

    &-details {
        display: flex;
        align-items: center;
        flex: 1;
    }

    &-icon {
        margin-right: 10px;
        color: #666;
    }

    &-input {
        flex: 1;

        &-label {
            font-size: 13px;
            font-weight: 600;
            color: #333;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        &-val {
            font-size: 14px;
            color: #555;
            line-height: 1.4;

            &.add-link {
                color: #007bff;
                cursor: pointer;
                font-weight: 500;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }

    &-actions {
        display: flex;
        gap: 1px; // Butonlar arasındaki boşluğu 1px yaptık

        .action-button {
            cursor: pointer;
            border: none;
            background: none;
            padding: 2px; // Padding'i azalttık
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.3s;

            .material-icons {
                font-size: 12px; // İkon boyutunu 12px yaptık
                transition: color 0.3s;
            }

            &.edit-btn {
                .material-icons {
                    color: #4caf50;
                }

                &:hover {
                    background-color: rgba(76, 175, 80, 0.1);
                }
            }

            &.delete-btn {
                .material-icons {
                    color: #f44336;
                }

                &:hover {
                    background-color: rgba(244, 67, 54, 0.1);
                }
            }

            &.copy-btn {
                .material-icons {
                    color: #2196f3;
                }

                &:hover {
                    background-color: rgba(33, 150, 243, 0.1);
                }
            }
        }
    }

    .tags-container {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
    }

    .tag-label {
        display: inline-flex;
        align-items: center;
        border-radius: 12px;
        padding: 2px 8px;
        margin-right: 5px;
        font-size: 12px;
        max-width: 100%;
        flex-shrink: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .remove-tag {
            margin-right: 5px;
            cursor: pointer;
            font-weight: bold;
        }
    }
}
</style>

<style lang="scss">
#contact-property-modal-content {
    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 15px;
    }

    input {
        width: 100%;
        padding: 8px;
        margin-bottom: 15px;
        border: 1px solid #ccc;
        border-radius: 4px;
    }

    p {
        margin-bottom: 15px;
    }

    .email-suggestions {
        display: flex;
        flex-direction: column;
        gap: 5px;
        margin-top: 10px;

        span {
            cursor: pointer;
            color: #007bff;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}
</style>
